import React, { useEffect, useState } from "react";
import { Popover, OverlayTrigger } from "react-bootstrap";
import { defineMessages } from "react-intl";
import PropTypes from 'prop-types';
import { getCookieConsentValue, Cookies } from "react-cookie-consent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { far } from "@fortawesome/pro-regular-svg-icons";

// Hooks
import useEnv from "../hooks/useEnv";

// Sentences to be translated
export const messages = defineMessages({
    supportChatUsesCookies: {
        id: "Chat käyttää evästeitä",
        defaultMessage: "Chat käyttää evästeitä"
    },
    acceptCookiesToUseTheChat: {
        id: "Hyväksy evästeet käyttääksesi Chattia",
        defaultMessage: "Hyväksy evästeet käyttääksesi Chattia"
    }
});

/**
 * Adds the Dynatrace script to the page (if not already added).
 */
function addDynatrace() {
    const scripts = document.getElementsByTagName('script');
    const url = import.meta.env.BASE_URL + "js/dynatrace.js";

    if (!(scripts[0].src).includes(url)){
        let script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;

        let s = document.getElementsByTagName('script')[0];
        s.parentNode.insertBefore(script, s);
    }
}

/**
 * Removes the Dynatrace script from the page.
 */
function removeDynatrace() {
    const scripts = document.getElementsByTagName('script');
    const url = import.meta.env.BASE_URL + "js/dynatrace.js";

    for (const script of scripts) {
        if ((script.src).includes(url)) {
            script.remove();
        }
    }
}

/**
 * Set or remove cookies.
 * In addition, toggle Dynatrace on / off based on the cookies (accepted / declined).
 *
 * @param {*} value Cookie consent value (true/false)
 * @param {*} dynatrace is use of Dynatrace allowed (true/false)?
 */
function updateCookies(value, dynatrace) {
    if (value) {
        Cookies.set('CookieConsent', 'true', { expires: 365 });

        // also add Dynatrace if allowed in the environment
        if (dynatrace) {
            addDynatrace();
        }

    } else {
        Cookies.remove('CookieConsent');

        // also remove Dynatrace if allowed in the environment
        if (dynatrace) {
            removeDynatrace();
        }
    }
}


const CookieConsent = (props) => {
    const { isEnvTruthy } = useEnv();
    const lng = props.intl.formatMessage;
    const [cookieConsentChecked, setCookieConsentChecked] = useState(getCookieConsentValue());
    const [showPopover, setShowPopover] = useState(false);
    const [privacySettingsActive, setPrivacySettingsActive] = useState(false);
    const [buttonClasses, setButtonClasses] = useState(props.buttonStyling);

    let dynatrace = false;

    if (isEnvTruthy('DYNATRACE')) {
        dynatrace = true;
    }

    useEffect(() => {

        // if the environment allows the use of Dynatrace and cookies have already been accepted, add Dynatrace script
        if (dynatrace && cookieConsentChecked) {
            addDynatrace();
        }

    }, []);


    useEffect(() => {
        const styles = props.buttonStyling;
        setButtonClasses(styles);

        if (props.openedFrom === 'footer' && privacySettingsActive) {
            setButtonClasses(styles + ' active');
        }
    });


    /**
     * Hide / show the cookie consent popover, and handle style changes.
     * Also relay the information to the parent component for additional 
     * actions (e.g. open chat).
     * 
     * @param {*} e Popover visible or not
     */
    function handlePopoverToggle(e) {
        if (props.openedFrom !== 'footer' && getCookieConsentValue()) {
            // because cookies have already been accepted, open chat instead of the popover 
            setShowPopover(false);
            props.cookiesUpdated();
        } else {
            // style footer button differently if popover is open
            if (props.openedFrom === 'footer' && e) {
                setPrivacySettingsActive(true);
            } else if (props.openedFrom === 'footer' && !e) {
                setPrivacySettingsActive(false);
            }

            // hide / show popover
            setShowPopover(e);
        }
    }

    /**
     * Handle changes in the cookie consent.
     */
    const handleCookieConsentChange = () => {
        // update cookies
        if (document.querySelector('#popOver').checked === true) {
            updateCookies(true, dynatrace);
        } else {
            updateCookies(false, dynatrace);
        }
        setCookieConsentChecked(!cookieConsentChecked);
        // call parent function (e.g. to open chat or to remove disabled states from buttons), and toggle popover visibility
        props.cookiesUpdated(!cookieConsentChecked);
        handlePopoverToggle();
    };

    /**
     * Cookie consent popover content
     */
    const cookieConsentPopover = (
        <Popover id="cookies" className={props.popoverStyling} aria-label={lng(messages.acceptCookiesToUseTheChat)}>
            <Popover.Header as="h3">{lng(messages.supportChatUsesCookies)}</Popover.Header>
            <Popover.Body>
                <label>
                    <input
                        id='popOver'
                        className='me-2'
                        type="checkbox"
                        checked={!!getCookieConsentValue()}
                        onChange={handleCookieConsentChange}
                        title={lng(messages.acceptCookiesToUseTheChat)}
                        value={cookieConsentChecked}
                    />
                    {lng(messages.acceptCookiesToUseTheChat)}
                </label>
            </Popover.Body>
        </Popover>
    );


    return (
        <OverlayTrigger
            key={'cookieconsent'}
            trigger="click"
            placement="top"
            rootClose={true}
            show={showPopover}
            onToggle={handlePopoverToggle}
            overlay={cookieConsentPopover}
        >
            <button id={ props.buttonId } title={ props.buttonTitle } className={ buttonClasses } aria-label={props.buttonText}>
                { props.openedFrom === 'footer' ? <FontAwesomeIcon icon={ far.faGear } aria-hidden="true"/> : null } { props.buttonText }
                { props.openedFrom === 'invoice' && (<span className='float-end'> <FontAwesomeIcon icon={ far.faChevronRight } aria-hidden="true" /></span>) }
            </button>
        </OverlayTrigger>
    );
};

CookieConsent.propTypes = {
    'intl': PropTypes.object.isRequired,
    'openedFrom': PropTypes.string.isRequired,
    'buttonId': PropTypes.string,
    'buttonTitle': PropTypes.string,
    'buttonStyling': PropTypes.string,
    'buttonText': PropTypes.string,
    'popoverStyling': PropTypes.string,
    'cookiesUpdated': PropTypes.func
};

export default CookieConsent;
